import { useCallback, useState } from 'react';
import { useAssistantApi } from '../providers/assistant';
import { useToastContext } from '../providers/toast';
import { type ChatMessage, useAssistantStore } from '../stores/assistant-store';
import { getResponseErrorMessage } from '../utils/response';

// Mirrors services/assistant/src/config.ts CHAT_MAX_HISTORY / CHAT_MAX_MESSAGE_LEN.
// No shared export exists across the service boundary, so these are duplicated
// here to validate client-side before the request ever leaves the browser.
const CHAT_MAX_HISTORY = 20;
const CHAT_MAX_MESSAGE_LEN = 4000;

export type ChatError =
  | { type: 'unauthorized' }
  | { type: 'validation'; message: string }
  | { type: 'retryable'; message: string };

export const useAssistantChat = () => {
  const { client } = useAssistantApi();
  const { showToast } = useToastContext();
  const messages = useAssistantStore((s) => s.messages);
  const addMessage = useAssistantStore((s) => s.addMessage);
  const resetMessages = useAssistantStore((s) => s.reset);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      if (trimmed.length > CHAT_MAX_MESSAGE_LEN) {
        const message = `Message is too long (max ${CHAT_MAX_MESSAGE_LEN} characters).`;
        setError({ type: 'validation', message });
        showToast(message, 'error');
        return;
      }

      const history = messages.slice(-CHAT_MAX_HISTORY);
      addMessage({ role: 'user', content: trimmed });
      setError(null);
      setIsLoading(true);

      try {
        const response = await client.v1.chat.$post({ json: { message: trimmed, history } });

        if (response.status === 401) {
          setError({ type: 'unauthorized' });
          showToast('Your session has expired. Please sign in again.', 'error');
          return;
        }

        if (response.status === 400) {
          const message = await getResponseErrorMessage(response, 'Invalid request.');
          setError({ type: 'validation', message });
          showToast(message, 'error');
          return;
        }

        if (!response.ok) {
          const message = await getResponseErrorMessage(
            response,
            'Something went wrong. Please try again.'
          );
          setError({ type: 'retryable', message });
          showToast(message, 'error');
          return;
        }

        const { answer } = await response.json();
        addMessage({ role: 'assistant', content: answer });
      } catch {
        const message = 'Network error. Please try again.';
        setError({ type: 'retryable', message });
        showToast(message, 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [client, messages, addMessage, showToast]
  );

  const reset = useCallback(() => {
    resetMessages();
    setError(null);
  }, [resetMessages]);

  return { messages, send, isLoading, error, reset };
};

export type { ChatMessage };
